// Types for ImpactAI Presets Management

export type ContextValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | ObjectContextValue
  | ArrayContextValue;

export interface ObjectContextValue {
  [key: string]: ContextValue;
}

export type ArrayContextValue = Array<ContextValue>;

export interface ContextStore {
  [key: string]: ContextValue;
}

export interface MessageAction {
  type: 'message';
  data: {
    text: string;
    actor: 'bot' | 'user';
    delay?: number;
  };
}

export interface ChoiceOption {
  text: string;
  backgroundColor?: string;
  textColor?: string;
  icon?: string;
  iconColor?: string;
  value: string;
}

export interface ChoiceAction {
  type: 'choice';
  data: {
    choiceId: string;
    choices: Record<string, ChoiceOption>;
  };
}

export interface MultipleChoiceOption {
  text: string;
  value: string;
}

export interface MultipleChoiceAction {
  type: 'multipleChoice';
  data: {
    choiceId: string;
    choices: MultipleChoiceOption[];
    minSelections?: number;
    maxSelections?: number;
    submitButtonText?: string;
  };
}

export interface TemplateInputAction {
  type: 'templateInput';
  data: {
    template: string;
  };
}

export interface NavigatePayload {
  url: string;
}

export interface SetFormFieldsPayload {
  form: string;
  fields: Record<string, unknown>;
  streamingDelay?: number;
}

export interface SetContextPayload {
  context: Record<string, ContextValue>;
}

export interface SearchNonprofitsPayload {
  outputPath: string;
  searchTerm?: string;
  acceptsDonations?: boolean;
  causes?: string[];
  countryCode?: string;
  stateCode?: string;
  donationProvider?: string;
  excludeDonationProviders?: string[];
  includeApproved?: boolean;
  limit?: number;
  offset?: number;
  waitForCompletion?: boolean;
}

export interface GeneratePayload {
  generationId: string;
  [key: string]: unknown;
}

export interface LoadNonprofitsPayload {
  inputPath: string;
  outputPath: string;
  waitForCompletion?: boolean;
}

export interface LoadCausesPayload {
  sdgsOutputPath?: string;
  categoriesOutputPath?: string;
  waitForCompletion?: boolean;
}

export interface LoadPicturesPayload {
  outputPath?: string;
  waitForCompletion?: boolean;
}

export interface LoadUserPayload {
  outputPath?: string;
  waitForCompletion?: boolean;
}

export interface WaitForContextPayload {
  contextPaths: string[];
  checkAllPaths?: boolean;
  timeout?: number;
  pollingInterval?: number;
}

export interface DebugPayload {
  message?: string;
  includeTimestamp?: boolean;
}

export interface DelayPayload {
  delay: number;
}

export interface ParseDataPayload {
  script: string;
  outputPath: string;
  inputPaths?: string[];
  useFullContext?: boolean;
  timeout?: number;
}

export interface ReduxGetOperation {
  reduxPath: string;
  contextKey: string;
}

export interface ReduxSetOperation {
  value: any;
  reduxPath: string;
}

export interface ReduxPayload {
  get?: ReduxGetOperation[];
  set?: ReduxSetOperation[];
}

export interface BatchGeneratePayload {
  form?: string;
  streamingDelay?: number;
  batches: Array<PresetBatch | GenerativeBatch>;
}

export interface PresetBatch {
  type: 'preset';
  fields: Record<string, unknown>;
}

export interface GenerativeBatch {
  type: 'generative';
  target: 'form' | 'context';
  generationId: string;
  contextToPromptMap: Array<{
    source?: string;
    value?: unknown;
    target: string;
  }>;
  outputToFieldMap?: Array<{
    source: string;
    target: string;
  }>;
}

export interface CallActionUI {
  showSpinner?: boolean;
  startMessage?: string;
  endMessage?: string;
}

export interface CallAction {
  type: 'call';
  data: {
    method:
      | 'navigate'
      | 'set-form-fields'
      | 'set-context'
      | 'search-nonprofits'
      | 'generate'
      | 'batch-generate'
      | 'load-nonprofits'
      | 'load-causes'
      | 'load-pictures'
      | 'load-user'
      | 'set-default-picture'
      | 'wait-for-context'
      | 'debug'
      | 'delay'
      | 'parse-data'
      | 'redux';
    payload: CallActionPayload;
    ui?: CallActionUI;
  };
}

export interface SubPresetAction {
  type: 'subPreset';
  data: {
    presetId: string;
    // These fields are added during expansion
    sourcePresetId?: string;
    sourcePresetTitle?: string;
  };
}

export interface LocationInputAction {
  type: 'locationInput';
  data: {
    key: string;
    isString?: boolean;
  };
}

export interface SetDefaultPicturePayload {
  form: string;
  categories: string[];
}

export type CallActionPayload =
  | NavigatePayload
  | SetFormFieldsPayload
  | SetContextPayload
  | SearchNonprofitsPayload
  | GeneratePayload
  | BatchGeneratePayload
  | LoadNonprofitsPayload
  | LoadCausesPayload
  | LoadPicturesPayload
  | LoadUserPayload
  | SetDefaultPicturePayload
  | WaitForContextPayload
  | DebugPayload
  | DelayPayload
  | ParseDataPayload
  | ReduxPayload;

export type PresetAction =
  | MessageAction
  | ChoiceAction
  | MultipleChoiceAction
  | TemplateInputAction
  | CallAction
  | SubPresetAction
  | LocationInputAction;

export interface Preset {
  id: string;
  title: string;
  description: string;
  iconName: string;
  color: string;
  enabled?: boolean;
  actions: PresetAction[];
  initialContext?: ContextStore;
  relatedPresets?: string[]; // Array of preset IDs that should be included in the query
  requiredPrivileges?: string[]; // Array of privileges required to access this preset
  sortIndex?: number; // Sort order index for displaying presets
}

// Alias for backward compatibility
export type ImpactAiPreset = Preset;

export interface PresetFormData {
  title: string;
  description: string;
  iconName: string;
  color: string;
  enabled: boolean;
  relatedPresets: string[];
  requiredPrivileges: string[];
  sortIndex?: number;
}

// Icon type - any valid MUI icon name
export type IconName = string;

export const ACTION_TYPES = {
  MESSAGE: 'message' as const,
  CHOICE: 'choice' as const,
  MULTIPLE_CHOICE: 'multipleChoice' as const,
  TEMPLATE_INPUT: 'templateInput' as const,
  CALL: 'call' as const,
  SUB_PRESET: 'subPreset' as const,
  LOCATION_INPUT: 'locationInput' as const,
} as const;

export const ACTOR_TYPES = {
  BOT: 'bot' as const,
  USER: 'user' as const,
} as const;

export const CALL_METHODS = {
  NAVIGATE: 'navigate' as const,
  SET_FORM_FIELDS: 'set-form-fields' as const,
  SET_CONTEXT: 'set-context' as const,
  SEARCH_NONPROFITS: 'search-nonprofits' as const,
  GENERATE: 'generate' as const,
  BATCH_GENERATE: 'batch-generate' as const,
  LOAD_NONPROFITS: 'load-nonprofits' as const,
  LOAD_CAUSES: 'load-causes' as const,
  LOAD_PICTURES: 'load-pictures' as const,
  LOAD_USER: 'load-user' as const,
  SET_DEFAULT_PICTURE: 'set-default-picture' as const,
  WAIT_FOR_CONTEXT: 'wait-for-context' as const,
  DEBUG: 'debug' as const,
  DELAY: 'delay' as const,
  PARSE_DATA: 'parse-data' as const,
  REDUX: 'redux' as const,
} as const;

export const DEFAULT_PRESET: Partial<ImpactAiPreset> = {
  title: '',
  description: '',
  iconName: 'AutoAwesome',
  color: '#6200EE',
  enabled: true,
  actions: [],
  initialContext: {},
  relatedPresets: [],
  requiredPrivileges: [],
};

// Campaign Form Field Names - based on README documentation
export const CAMPAIGN_FORM_FIELDS = {
  // Basic Info
  TITLE: 'title',
  DESCRIPTION: 'description',
  CTA: 'cta',
  VIDEO_LINK: 'videoLink',

  // Content
  TOTAL_HEADLINE: 'totalHeadline',
  TOTAL_TEXT: 'totalText',

  // Styling
  COLOR_A: 'colorA',
  COLOR_B: 'colorB',
  NAME_COLOR: 'nameColor',
  CTA_BUTTON_COLOR: 'ctaButtonColor',
  CTA_TEXT_COLOR: 'ctaTextColor',
  BACKGROUND_IMAGE: 'backgroundImage',
  BACKGROUND_HERO_COLOR: 'backgroundHeroColor',
  BACKGROUND_DEEDS_COLOR: 'backgroundDeedsColor',

  // Content Selection
  NONPROFITS: 'nonprofits',
  FUNDRAISERS: 'fundraisers',
  VOLUNTEERING: 'volunteering',
  EVENTS: 'events',

  // Goals & Settings
  GOAL_AMOUNT: 'goalAmount',
  GOAL_CURRENCY_CODE: 'goalCurrencyCode',
  HIDE_PROGRESS_BAR: 'hideProgressBar',
  HIDE_ACTIVITY_FEED: 'hideActivityFeed',

  // Visibility & Location
  PRIVACY: 'privacy',
  LOCATIONS_EXCLUDE: 'locationsExclude',
  LOCATIONS: 'locations',

  // Dates
  STARTING_AT: 'startingAt',
  ENDING_AT: 'endingAt',
  PLEDGED_DONATION_STARTING_AT: 'pledgedDonationStartingAt',
  PLEDGED_DONATION_ENDING_AT: 'pledgedDonationEndingAt',

  // Advanced Settings
  ALLOW_ONE_TIME_PLEDGE: 'allowOneTimePledge',
  SORTING: 'sorting',
  LINK_ONLY: 'linkOnly',
  CONTENT_LOCATION_FILTERING: 'contentLocationFiltering',
  CONTENT_SORTING_SECTIONS: 'contentSortingSections',
} as const;

// Campaign page navigation sections
export const CAMPAIGN_SECTIONS = {
  HEADER: 'header',
  DESCRIPTION: 'description',
  STYLING: 'styling',
  NONPROFITS: 'nonprofits',
  FUNDRAISERS: 'fundraisers',
  VOLUNTEERING: 'volunteering',
  EVENTS: 'events',
  ACTIONS: 'actions',
  GENERAL: 'general',
} as const;

// Helper type for template variables
export type TemplateVariable = `{{${string}}}` | `{{${string},${string}}}`;

// Helper type for context references
export type ContextReference = `$context:${string}`;
